import clsx from 'clsx'
import type { ReactElement, ReactNode } from 'react'
import { View } from 'react-native'

import { ActionSheet } from '@/src/elements/action'
import type { TActionSheetProps } from '@/src/elements/action/ActionSheetMobile'
import { Avatar } from '@/src/elements/avatar'
import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardFooter, CardHeader } from '@/src/elements/card'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { useTranslate } from '@/src/lang/useTranslate'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { INTENTION_TYPE } from '../../../models/Intention.enums'
import type { TTypeIntention } from '../../../models/Intention.types'
import { getIntentionUserName } from '../../../utils/getIntentionUserName'
import {
  intentionCardVariants,
  type TIntentionCardSlots,
  type TIntentionCardVariantProps,
} from './intentionCardVariants'

const PARTY_EMOJI = ' 🥳'

export type TIntentionCardProps<Type extends INTENTION_TYPE> = {
  className?: string
  classNames?: SlotsToClasses<TIntentionCardSlots>
  hideBody?: boolean
  options?: TActionSheetProps['items']
  showUser?: boolean
  intention?: TTypeIntention<Type>
  type: Type
  children?: ReactNode
} & Omit<TIntentionCardVariantProps, 'type'>

export const IntentionCard = <Type extends INTENTION_TYPE>({
  className,
  classNames: { text: textClassName, avatarPlaceholder: avatarPlaceholderClassName, ...cardClassNames } = {},
  color,
  children,
  intention,
  isLoading,
  hideBody,
  options,
  showUser,
  type,
}: TIntentionCardProps<Type>): ReactElement | false | undefined => {
  const { t } = useTranslate()

  const slots = intentionCardVariants({ isLoading, color, type })

  const getText = () => {
    switch (intention?.type) {
      case INTENTION_TYPE.INTENTION:
        return intention?.content
      case INTENTION_TYPE.ARRIVAL:
        return t('intention.arrival.welcome-pray')
      case INTENTION_TYPE.BIRTHDAY:
        return t('intention.birthday.pray')
      default:
        return ''
    }
  }

  const birthdayTitle = (name: string, birthday: string) => {
    return t('intention.birthday.celebration', {
      username: name,
      years: new Date(Date.now()).getFullYear() - new Date(birthday).getFullYear(),
    })
  }

  const cardTestId = intention ? TEST_ID.INTENTIONS.CARD.BASE(type, intention.id) : TEST_ID.INTENTIONS.CARD.LOADING

  const userName = getIntentionUserName(intention, t('intention.anonymous-praying'))

  return (
    (intention || isLoading) && (
      <Card shadow="sm" className={slots.base({ className })} classNames={cardClassNames} testID={cardTestId}>
        {!!options && !!intention && (
          <ActionSheet items={options}>
            <Button
              size="xs"
              isIconOnly
              startIcon={ICON.ELLIPSIS_VERTICAL}
              colors="default"
              variant="light"
              contrast="low"
              className="absolute right-1.5 top-1.5 z-20"
              testID={TEST_ID.INTENTION.OPTIONS.BUTTON}
            />
          </ActionSheet>
        )}
        {!!showUser && (
          <CardHeader className={slots.header()}>
            {intention && (
              <>
                <Avatar src={getAvatarUrl(intention.user?.avatar)} />
                {intention.type === INTENTION_TYPE.INTENTION && (
                  <Typography
                    bold
                    numberOfLines={2}
                    className={slots.text({
                      className: clsx(
                        'min-w-0 shrink overflow-hidden break-words',
                        { 'pr-8': !!options },
                        textClassName,
                      ),
                    })}
                  >
                    {userName}
                  </Typography>
                )}
                {intention.type === INTENTION_TYPE.ARRIVAL && (
                  <Typography
                    bold
                    numberOfLines={2}
                    className={slots.text({
                      className: clsx('min-w-0 shrink overflow-hidden break-words', textClassName),
                    })}
                  >
                    {t('intention.arrival.just-joined', { userName })}
                  </Typography>
                )}
                {intention.type === INTENTION_TYPE.BIRTHDAY && (
                  <View className="min-w-0 flex-1 shrink overflow-hidden">
                    <GradientTypography
                      variant="gradient"
                      multicolor
                      bold
                      numberOfLines={2}
                      className="min-w-0 break-words"
                      classNameWrapper="min-w-0 native:flex-row native:items-start native:justify-start"
                      unaffectedChildren={
                        <Typography
                          ariaRole="span"
                          bold
                          className={className + ' native:flex-none'}
                          role="img"
                          aria-label={t('user.happy-birthday.wish')}
                        >
                          {PARTY_EMOJI}
                        </Typography>
                      }
                    >
                      {birthdayTitle(userName, intention.user.birthdate)}
                    </GradientTypography>
                  </View>
                )}
              </>
            )}
            {!intention && <View className={slots.avatarPlaceholder({ className: avatarPlaceholderClassName })} />}
          </CardHeader>
        )}
        {!hideBody && (
          <CardBody className={slots.body()}>
            <Typography
              className={clsx(slots.text({ className: textClassName }), {
                'text-primary-text-contrast-default': type !== INTENTION_TYPE.INTENTION,
              })}
            >
              {getText()}
            </Typography>
          </CardBody>
        )}
        {!!children && <CardFooter className={slots.footer()}>{children}</CardFooter>}
      </Card>
    )
  )
}
